import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu } from 'lucide-react';
import Sidebar from './Sidebar';
import ConfirmModal from '../common/ConfirmModal';
import { useAuth } from '../../context/AuthContext';
import { fetchOrders, fetchInquiries } from '../../utils/api';

/**
 * 관리자 레이아웃
 * 개선사항:
 * - 이중 패딩(p-6 중복) 제거: 각 페이지가 자체 p-6 을 가지므로 레이아웃은 패딩을 두지 않는다.
 * - 상단 헤더바(관리자명 표시 + 로그아웃) 추가.
 * - 모바일 사이드바 토글(햄버거) 지원.
 * - 로그아웃 시 ConfirmModal 로 확인 후 useAuth().logout (localStorage 정리) + /admin/login 이동.
 * - 사이드바 미답변/대기 배지 카운트 집계.
 */
const AdminLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [badges, setBadges] = useState({ orders: 0, inquiries: 0 });

  // 라우트 변경 시 모바일 사이드바 자동 닫기
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // 사이드바 배지 카운트(처리 대기 주문 / 미답변 문의) 집계
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [orders, inquiries] = await Promise.all([fetchOrders(), fetchInquiries()]);
        if (!active) return;
        const pendingOrders = orders.filter(
          (o) => o.orderStatus === 'pending' || o.orderStatus === 'preparing'
        ).length;
        const pendingInquiries = inquiries.filter((i) => i.status === 'pending').length;
        setBadges({ orders: pendingOrders, inquiries: pendingInquiries });
      } catch (e) {
        // 배지는 부가 정보이므로 실패해도 무시
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        onLogout={() => {
          setSidebarOpen(false);
          setLogoutOpen(true);
        }}
        badges={badges}
      />

      <div className="flex min-w-0 flex-1 flex-col">
        {/* 상단 헤더바 */}
        <header className="sticky top-0 z-20 flex items-center justify-between border-b border-gray-200 bg-white px-4 py-3 lg:px-6">
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            className="text-gray-600 hover:text-gray-900 lg:hidden"
            aria-label="메뉴 열기"
          >
            <Menu size={24} />
          </button>

          <div className="ml-auto flex items-center gap-4">
            <span className="hidden text-sm text-gray-600 sm:inline">
              <span className="font-medium text-gray-900">{user?.name || '관리자'}</span> 님
            </span>
            <button
              type="button"
              onClick={() => setLogoutOpen(true)}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100"
            >
              로그아웃
            </button>
          </div>
        </header>

        <main className="flex-1 overflow-auto">{children}</main>
      </div>

      <ConfirmModal
        isOpen={logoutOpen}
        onClose={() => setLogoutOpen(false)}
        onConfirm={handleLogout}
        title="로그아웃"
        message="관리자 계정에서 로그아웃하시겠습니까?"
        confirmLabel="로그아웃"
        confirmVariant="danger"
      />
    </div>
  );
};

export default AdminLayout;
